// Transactions.cshtml: every card transaction of the admin's scope, filtered, 20 a page.
import { money, shortDate } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi, totalPages } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { toQuery, useFilterForm, useQueryParams } from '../components/filters';
import { useStaff } from '../components/RequireStaff';
import { ChooseProcessor, Pagination, StatusBadge } from '../components/ui';
import { clientOptions, effectiveProgramId, effectiveProvider, PROVIDER_OPTIONS, TRANSACTION_TYPES } from '../scope';

const PAGE_SIZE = 20;

export function TransactionsPage() {
  usePageTitle('Transactions');
  const { scope } = useStaff();
  const q = useQueryParams();
  const f = {
    search: q.get('search'),
    provider: q.get('provider'),
    type: q.get('type'),
    programId: q.get('program'),
  };
  const page = q.page();
  const submit = useFilterForm('/transactions');
  const results = useQuery({
    queryKey: ['transactions', scope.activeProvider, f, page],
    enabled: !scope.needsProvider,
    queryFn: () =>
      adminApi.transactions({
        search: f.search,
        provider: effectiveProvider(scope, f.provider),
        type: f.type,
        programId: effectiveProgramId(scope, f.programId),
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const programs = useQuery({ queryKey: ['programs'], queryFn: adminApi.programs, enabled: !scope.needsProvider });
  const filtered = Object.values(f).some((v) => v !== null);
  const data = results.data;

  return (
    <>
      <div className="qc-page-heading">
        <h1>Transactions</h1>
        <p className="qc-breadcrumb">
          QOR Console <span aria-hidden="true">&rsaquo;</span> <strong>Transactions</strong>
        </p>
      </div>

      {scope.internal && (
        <p className="qc-breadcrumb" style={{ marginBottom: 12 }}>
          Showing <strong>{scope.activeProvider ?? 'no processor chosen'}</strong> transactions only
        </p>
      )}

      {scope.needsProvider ? (
        <ChooseProcessor what="to see its transactions" />
      ) : (
        <>
          {(!scope.internal || programs.isSuccess) && (
            <form className="qc-cardholder-filters" onSubmit={submit} key={toQuery(f)}>
              <div className="qc-search-wrap">
                <input
                  type="search"
                  name="search"
                  defaultValue={f.search ?? ''}
                  placeholder="Search by cardholder, email, or description..."
                  autoComplete="off"
                />
              </div>
              {scope.internal ? (
                <select name="program" aria-label="Filter by partner" defaultValue={f.programId ?? ''}>
                  <option value="">All partners</option>
                  {clientOptions(programs.data ?? [], scope).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                    </option>
                  ))}
                </select>
              ) : (
                <select name="provider" aria-label="Filter by processor" defaultValue={f.provider ?? ''}>
                  <option value="">All processors</option>
                  {PROVIDER_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              )}
              <select name="type" aria-label="Filter by transaction type" defaultValue={f.type ?? ''}>
                <option value="">All types</option>
                {TRANSACTION_TYPES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <button type="submit" className="qor-button qor-button-primary">
                Filter
              </button>
              {filtered && (
                <Link className="qc-button-ghost" to="/transactions">
                  Clear
                </Link>
              )}
            </form>
          )}

          <div className="qc-panel">
            {!data ? null : data.items.length === 0 ? (
              <p className="qc-empty">
                No transactions match these filters. Transactions appear here once a processor&apos;s transaction
                webhook events start posting.
              </p>
            ) : (
              <>
                <table className="qc-plain-table">
                  <thead>
                    <tr>
                      <th>Cardholder</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Processor</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((t) => (
                      <tr key={t.id}>
                        <td>
                          {t.cardholderName}
                          <br />
                          <small>{t.cardholderEmail}</small>
                        </td>
                        <td>{t.description}</td>
                        <td>{t.transactionType}</td>
                        <td>{t.provider}</td>
                        <td>
                          {money(t.amount, t.currency)} {t.currency}
                        </td>
                        <td>
                          <StatusBadge status={t.status} good="Posted" />
                        </td>
                        <td>{shortDate(t.postedAt) ?? 'Pending'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination
                  page={data.page}
                  totalPages={totalPages(data)}
                  totalCount={data.totalCount}
                  href={(p) =>
                    `/transactions${toQuery({ search: f.search, provider: f.provider, type: f.type, program: f.programId, page: p })}`
                  }
                />
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
